import type { RankBoard, RankingsResponse } from '@shared/protocol/messages';
import type { App } from '../app/App';
import { t } from '../i18n';
import { modal } from './components';
import { h, setChildren } from './dom';

/**
 * Server rankings: online trophies, story stars, the fastest whole-story
 * finish, the Homework Tower and hero level. One board at a time, the top 50
 * with medals, and the player's own place pinned underneath.
 */

interface BoardDef {
  id: RankBoard;
  icon: string;
  label: string;
  /** What the score means, under the tabs. */
  hint: string;
  format: (score: number) => string;
}

/** 1 h 05 min / 12 min. */
export function formatRunTime(ms: number): string {
  const min = Math.round(ms / 60000);
  const hours = Math.floor(min / 60);
  return hours > 0 ? t('{h} h {m} min', { h: hours, m: String(min % 60).padStart(2, '0') }) : t('{m} min', { m: min });
}

export const BOARDS: BoardDef[] = [
  { id: 'trophies', icon: '🏆', label: 'Online', hint: 'Trophies from public online battles. The server decides every battle.', format: (n) => `${n.toLocaleString()} 🏆` },
  { id: 'stars', icon: '⭐', label: 'Stars', hint: 'Story stars collected (90 is perfect: three stars on all 30 stages).', format: (n) => `${n} / 90 ⭐` },
  { id: 'speedrun', icon: '⏱️', label: 'Fastest story', hint: 'Time from the first story battle to finishing the last chapter. Shortest wins.', format: formatRunTime },
  { id: 'tower', icon: '🗼', label: 'Tower', hint: 'Highest floor climbed in the Homework Tower.', format: (n) => t('Floor {n}', { n }) },
  { id: 'level', icon: '📈', label: 'Level', hint: 'Highest hero level.', format: (n) => t('Lv. {n}', { n }) },
];

const MEDAL = ['🥇', '🥈', '🥉'];

/** Boards fetched in the last minute, reused when a page redraws (the server reads the database for them). */
const CACHE_MS = 60_000;
const cache = new Map<RankBoard, { at: number; res: RankingsResponse }>();

/** The rankings, as a block to put in a page (or in the modal below). */
export function rankingsPanel(app: App, start: RankBoard = 'trophies', opts: { limit?: number; onMore?: () => void } = {}): HTMLElement {
  let board = start;
  const tabs = h('div', { class: 'rank-tabs', role: 'tablist', 'aria-label': t('Rankings') });
  const hint = h('p', { class: 'hand-hint rank-hint' });
  const list = h('div', { class: 'rank-body', 'aria-live': 'polite' });
  const meId = app.store.state.online?.playerId ?? null;

  const drawTabs = (): void =>
    setChildren(
      tabs,
      BOARDS.map((b) =>
        h('button', { type: 'button', role: 'tab', 'aria-selected': String(b.id === board), class: `rank-tab ${b.id === board ? 'on' : ''}`, on: { click: () => ((board = b.id), void load()) } }, h('span', { 'aria-hidden': 'true' }, b.icon), ' ', t(b.label)),
      ),
    );

  const draw = (res: RankingsResponse): void => {
    const def = BOARDS.find((b) => b.id === res.board) ?? BOARDS[0]!;
    const rows = opts.limit ? res.rows.slice(0, opts.limit) : res.rows;
    const row = (r: { rank: number; name: string; score: number }, mine: boolean): HTMLElement =>
      h(
        'li',
        { class: `rank-row ${mine ? 'me' : ''} ${r.rank <= 3 ? `top top-${r.rank}` : ''}` },
        h('span', { class: 'rank-no' }, MEDAL[r.rank - 1] ?? `#${r.rank}`),
        h('b', { class: 'rank-name' }, r.name),
        h('span', { class: 'rank-score' }, def.format(r.score)),
      );
    const meOnList = res.me !== null && rows.some((r) => r.id === meId);
    setChildren(
      list,
      rows.length ? h('ol', { class: 'rank-list2' }, rows.map((r) => row(r, r.id === meId))) : h('p', { class: 'hand-hint' }, t('Nobody is on this board yet. Be the first!')),
      res.me && !meOnList ? h('ol', { class: 'rank-list2 rank-mine' }, row({ rank: res.me.rank, name: t('You'), score: res.me.score }, true)) : null,
      !res.me ? h('p', { class: 'hand-hint' }, meId ? t('You are not on this board yet.') : t('Sign in to see your own place.')) : null,
      h('p', { class: 'hand-hint rank-total' }, t('{n} players on this board', { n: res.total })),
      opts.onMore ? h('button', { type: 'button', class: 'rank-more', on: { click: opts.onMore } }, t('See all rankings →')) : null,
    );
  };

  const load = async (): Promise<void> => {
    drawTabs();
    hint.textContent = t(BOARDS.find((b) => b.id === board)!.hint);
    setChildren(list, h('p', { class: 'hand-hint' }, t('Loading…')));
    const asked = board;
    try {
      const hit = cache.get(asked);
      let res: RankingsResponse;
      if (hit && Date.now() - hit.at < CACHE_MS) res = hit.res;
      else {
        app.api.token = app.store.state.online?.token ?? null;
        res = await app.api.rankings(asked);
        cache.set(asked, { at: Date.now(), res });
      }
      if (asked === board) draw(res);
    } catch {
      if (asked === board) setChildren(list, h('p', { class: 'hand-hint' }, t('Battle server offline — ranking unavailable.')));
    }
  };
  void load();
  return h('div', { class: 'rankings' }, tabs, hint, list);
}

/** The full rankings in a window. */
export async function openRankings(app: App, start: RankBoard = 'trophies'): Promise<void> {
  await modal(t('Rankings'), rankingsPanel(app, start), [{ label: t('Close'), value: 'ok', variant: 'primary', color: 'yellow' }], { className: 'rankings-modal' });
}
